/**
 * Where the emailed link lands: /signin/:token
 *
 * The backend URL is `${APP_URL}/signin/${token}`, so this route's shape is
 * fixed by what has already been deployed.
 *
 * Redemption is attempted exactly once. React 18+ runs effects twice in
 * development StrictMode, and the token is single-use — so a naive effect
 * spends the link on the first run and reports "already used" on the second,
 * which looks like a broken link on every local sign-in. The ref guard is the
 * fix, and it is not development-only paranoia: any remount would do the same.
 *
 * THE ORGANIZATION IS CHOSEN HERE, before anything else loads. A link is
 * asked for with an email address, and an address can belong to two
 * hospitals on the platform; the link itself cannot know which one the
 * person means. So once the link is redeemed the page asks which
 * organizations the account is in: one, and the session is pointed at it;
 * more than one, and the person picks from a list on this same card. Only
 * then does the app open. On the console host the question is skipped: the
 * console is not scoped to an organization.
 */

import { useEffect, useRef, useState } from 'react';
import { homeFor, isConsoleHost } from './home';
import { Navigate, useParams, Link } from 'react-router-dom';
import { redeemSignInLink } from './signInLink';
import { chooseOrganization, landing, listMyOrganizations, type MyOrganization } from './organizations';
import { isCallFailure } from '../lib/callable';
import { useAuth } from './useAuth';
import { clearDestination, peekDestination } from './returnTo';
import { Spinner } from '../app/Spinner';
import { ROLE_DISPLAY_NAMES, isRole } from '../types/roles';
import styles from './SignIn.module.css';
import { Wordmark } from '../shared/Wordmark';

type Step = 'redeeming' | 'choosing' | 'done';

function roleLabels(org: MyOrganization): string {
  const roles = org.roles.filter(isRole);
  return roles.length ? roles.map((r) => ROLE_DISPLAY_NAMES[r]).join(', ') : 'No role';
}

export function SignInCallbackPage() {
  const { token } = useParams<{ token: string }>();
  const { session, refresh } = useAuth();
  const [step, setStep] = useState<Step>('redeeming');
  const [orgs, setOrgs] = useState<MyOrganization[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState<string | null>(null);
  const attempted = useRef(false);
  // Read once, on mount, before anything clears it.
  const [returnTo] = useState(() => peekDestination());

  useEffect(() => {
    if (attempted.current || !token) return;
    attempted.current = true;

    (async () => {
      try {
        await redeemSignInLink(token);
        // The console needs no organization: a system administrator sees
        // every one of them. Asking "which organization?" on
        // console.standingdocs.com would be the customer app's question.
        if (isConsoleHost(window.location.hostname)) {
          await refresh();
          setStep('done');
          return;
        }
        const mine = await listMyOrganizations();
        const next = landing(mine);
        if (next.kind === 'one') {
          // Always pointed explicitly, so a stale claim from an organization
          // this person has since left cannot carry over.
          await chooseOrganization(next.orgId);
          await refresh();
          setStep('done');
        } else if (next.kind === 'choose') {
          setOrgs(mine);
          setStep('choosing');
        } else {
          setStep('done');
        }
      } catch (err: unknown) {
        setError(
          isCallFailure(err)
            ? err.message
            : 'That sign-in link could not be used. Please request a new one.',
        );
      }
    })();
  }, [token, refresh]);

  async function pick(orgId: string) {
    setChoosing(orgId);
    setError(null);
    try {
      await chooseOrganization(orgId);
      await refresh();
      setStep('done');
    } catch (err: unknown) {
      setError(isCallFailure(err) ? err.message : 'Could not open that organization.');
      setChoosing(null);
    }
  }

  /**
   * onAuthStateChanged fires once the custom token is exchanged; the app opens
   * only when the organization question is settled.
   *
   * AND IT OPENS WHERE THEY WERE GOING. The destination was stored by
   * `RequireAuth` when they were turned away, in this browser and nowhere
   * else, and is read once on mount rather than consumed: redemption can fail,
   * and somebody asking for a second link should still land on the page they
   * were trying to reach. It is cleared at the moment they get there.
   */
  if (session && step === 'done') {
    clearDestination();
    return <Navigate to={returnTo ?? homeFor(session)} replace />;
  }

  if (error && step !== 'choosing') {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <Wordmark withIcon className={styles.brandMark} />
          </div>
          <h1 className={styles.title}>This link no longer works</h1>
          <div className={styles.error} role="alert">
            {error}
          </div>
          <Link to="/signin" className={styles.submit} style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            Request a new link
          </Link>
        </div>
      </main>
    );
  }

  if (step === 'choosing') {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <Wordmark withIcon className={styles.brandMark} />
          </div>
          <h1 className={styles.title}>Which organization?</h1>
          <p className={styles.lede}>
            This address belongs to more than one. Everything you see comes from the one you pick; you can switch later from Settings.
          </p>
          {error && (
            <div className={styles.error} role="alert">{error}</div>
          )}
          <ul className={styles.orgList} aria-label="Your organizations">
            {orgs.map((org) => (
              <li key={org.orgId}>
                <button
                  type="button"
                  className={styles.orgChoice}
                  disabled={choosing !== null}
                  onClick={() => void pick(org.orgId)}
                >
                  <span className={styles.orgName}>{org.name}</span>
                  <span className={styles.orgRoles}>{choosing === org.orgId ? 'Opening…' : roleLabels(org)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </main>
    );
  }

  return <Spinner label="Signing you in" />;
}
